import React, { Component } from 'react';
import logo from './kingLogoDark.svg';
import './App.css';
import ErrorLayer from './components/ErrorLayer';
import Form from './components/Form';
import { FormDeleteAccount } from './components/Form/FormDeleteAccount.js';
import LegalInfo from './components/LegalInfo/LegalInfo';
import LoadingLayer from './components/LoadingLayer';
import SubmitSuccess from './components/SubmitSuccess';
import fieldValidation from './fieldValidation';
import requests from './requests.js';
import i18n from './i18n';
import { queryParameters } from './queryParameters';
import formsConfiguration from './components/Form/formsConfiguration';
import { updateOneTrustPreferences } from './utils/updateOneTrustPreferences.js';
import selectFieldsConfiguration from './components/Form/selectFieldsConfiguration';
import {
    FORM_INITIAL_STATE,
    GAME_QUERY,
    ISSUE_SUBTYPE_QUERY,
    ISSUE_TYPE_QUERY,
    LOCAL_STORAGE_AUTH_KEY,
    PLATFORM_QUERY,
    SESSION_KEY_DATA_QUERY,
    SOPORTO_KING_APP_ID,
    TYPE_QUERY
} from './constants';
import { AuthProvider } from './contexts/AuthContext.js';
import langFieldOptions from './components/Form/langFieldOptions';
import Login from './components/Login/index.js';
import Logout from './components/Logout/index.js';

const appSteps = {
    loading: 0,
    form: 1,
    submitError: 2,
    submitSuccess: 3,
    login: 4,
    rtbf: 5
};

const SENSITIVE_URL_PARAMS = ['sessionKeyData', 'sessionKey'];

export function sanitizeUrl(url) {
    if (!url) return '';
    try {
        const parsed = new URL(url);
        if (parsed.searchParams) {
            let sanitized = false;
            SENSITIVE_URL_PARAMS.forEach((param) => {
                if (parsed.searchParams.has(param)) {
                    parsed.searchParams.set(param, 'Redacted');
                    sanitized = true;
                }
            });
            return sanitized ? parsed.toString() : url;
        }
    } catch (e) {
        // Fall through to the string replacement below for older browsers/test environments.
    }
    return sanitizeUrlByStringReplacement(url);
}

function sanitizeUrlByStringReplacement(url) {
    return SENSITIVE_URL_PARAMS.reduce((sanitizedUrl, param) => {
        const pattern = new RegExp('([?&]' + param + '=)[^&#]*', 'g');
        return sanitizedUrl.replace(pattern, '$1Redacted');
    }, url);
}

class App extends Component {
    constructor(props) {
        super(props);
        this.state = {
            appStep: appSteps.form,
            formData: FORM_INITIAL_STATE,
            captchaToken: '',
            isValidationEnabled: false,
            lang: i18n.defaultLang,
            sessionKey: '',
            source: '',
            soportoFormType: '',
            referrer: sanitizeUrl(document.referrer),
            landingUrl: sanitizeUrl(window.location.href)
        };

        this.formType = this.getFormType();
        this.forceFormDataUpdate = this.forceFormDataUpdate.bind(this);
        this.onFieldUpdate = this.onFieldUpdate.bind(this);
        this.onFieldHide = this.onFieldHide.bind(this);
        this.onLanguageChange = this.onLanguageChange.bind(this);
        this.onMsgFieldUpdate = this.onMsgFieldUpdate.bind(this);
        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.onDeleteAccountRequest = this.onDeleteAccountRequest.bind(this);
        this.onSessionKeyUpdate = this.onSessionKeyUpdate.bind(this);
        this.onSourceUpdate = this.onSourceUpdate.bind(this);
        this.onSoportoFormTypeUpdate = this.onSoportoFormTypeUpdate.bind(this);
        this.showRtbfForm = this.showRtbfForm.bind(this);
        this.showLogin = this.showLogin.bind(this);
        this.fillFormDataFromResponse = this.fillFormDataFromResponse.bind(this);
        this.prepopulateFormData = this.prepopulateFormData.bind(this);
        this.prepopulateFormDataWithGameAndPlatform = this.prepopulateFormDataWithGameAndPlatform.bind(this);
        this.prepopulateFormDataWithIssueTypeAndIssueSubType =
                this.prepopulateFormDataWithIssueTypeAndIssueSubType.bind(this);
        this.clearFormData = this.clearFormData.bind(this);

        window.onCaptchaUpdate = this.onCaptchaUpdate.bind(this);
        window.onCaptchaExpire = this.onCaptchaExpire.bind(this);
    }

    componentDidMount() {
        const sessionKeyData = queryParameters.get(SESSION_KEY_DATA_QUERY);
        const gameInQuery = queryParameters.get(GAME_QUERY);
        const platformInQuery = queryParameters.get(PLATFORM_QUERY);
        const issueTypeInQuery = queryParameters.get(ISSUE_TYPE_QUERY);
        const issueSubTypeInQuery = queryParameters.get(ISSUE_SUBTYPE_QUERY);
        const typeQuery = queryParameters.get(TYPE_QUERY);
        const userLanguage = new Intl.Locale(navigator.language).language;
        const isUserLanguageValid = langFieldOptions.find((langFieldOption) => {
            return langFieldOption.id === userLanguage;
        });

        this.prepopulateFormData(gameInQuery, platformInQuery, issueTypeInQuery, issueSubTypeInQuery);
        const selectedLanguage = isUserLanguageValid ? userLanguage : i18n.defaultLang;
        this.onLanguageChange(null, selectedLanguage);

        if (typeQuery === 'rtbf') {
            this.setState({ appStep: appSteps.rtbf });
        }

        if (!sessionKeyData) {
            return;
        }

        requests.getUserData(sessionKeyData, this.fillFormDataFromResponse, (error) => null);
    }

    getFormType() {
        const formType = queryParameters.get(TYPE_QUERY);
        return !formType || !formsConfiguration.form[formType] ? 'default' : formType;
    }

    getFormData() {
        return {
            player: {
                email: this.state.formData.email.value,
                name: this.state.formData.name.value,
                language: this.state.lang,
                sessionKey: this.state.sessionKey
            },
            game: this.state.formData.game.value,
            issueType: this.state.formData.issueType.value,
            issueSubType: this.state.formData.issueSubType.value,
            message: this.state.formData.message.value,
            platform: this.state.formData.platform.value,
            source: this.state.source,
            soportoFormType: this.state.soportoFormType,
            referrer: this.state.referrer,
            landingUrl: this.state.landingUrl
        };
    }

    onCaptchaExpire() {
        this.setState({
            captchaToken: ''
        });
    }

    onCaptchaUpdate(captchaToken) {
        this.setState({
            captchaToken: captchaToken
        });
    }

    onSessionKeyUpdate(sessionKey) {
        this.setState({
            sessionKey: sessionKey
        });
    }

    onSourceUpdate(source) {
        this.setState({
            source: source
        });
    }

    onSoportoFormTypeUpdate(soportoFormType) {
        this.setState({
            soportoFormType: soportoFormType
        });
    }

    forceFormDataUpdate(newFormData) {
        const updatedData = Object.assign({}, this.state.formData, newFormData);
        this.setState({
            formData: updatedData
        });
    }

    clearFormData() {
        this.setState({
            formData: FORM_INITIAL_STATE
        });
    }

    onFieldUpdate(fieldId, value, fieldConfig, resetFieldId) {
        const isValid = fieldValidation.isValid(fieldConfig, value);
        const updatedData = Object.assign({}, this.state.formData, {
            [fieldId]: {
                value: value,
                isValid: isValid
            }
        });
        if (resetFieldId && this.state.formData[resetFieldId]) {
            Object.assign(updatedData, {
                [resetFieldId]: {
                    value: '',
                    isValid: false
                }
            });
        }

        this.setState({
            formData: updatedData
        });
    }

    onFieldHide(fieldId) {
        if (!this.state.formData[fieldId].isValid) {
            const updatedData = Object.assign({}, this.state.formData, {
                [fieldId]: {
                    isValid: true
                }
            });
            this.setState({
                formData: updatedData
            });
        }
    }

    onLanguageChange(fieldId, value) {
        const normalizedLang = i18n.normalizeLang(value);
        document.documentElement.dir = i18n.getTextDirection(normalizedLang);
        document.documentElement.lang = normalizedLang;

        this.setState(
            {
                lang: value
            },
            () => {
                this.onMsgFieldUpdate(this.state.formData.message.value);
            }
        );
    }

    onMsgFieldUpdate(msgValue) {
        const isValid = fieldValidation.isValidMsg(msgValue, this.state.lang);
        const updatedData = Object.assign({}, this.state.formData, {
            message: {
                value: msgValue,
                isValid: isValid
            }
        });

        this.setState({
            formData: updatedData
        });
    }

    onSubmitClick() {
        this.validateFormData() && this.submitFormData();
    }

    onDeleteAccountRequest() {
        this.validateFormData() && this.submitDeleteAccountRequest();
    }

    submitFormData() {
        this.setState({
            appStep: appSteps.loading
        });
        if (this.getFormData().issueSubType === 'personalised_ads_opt_out_do_not_sell_my_data') {
            requests.getJWT(
                    this.state.captchaToken,
                    this.getFormData().player.email,
                    async (response) => {
                        updateOneTrustPreferences(response);
                        this.setState({
                            appStep: appSteps.submitSuccess
                        });
                    },
                    () =>
                            this.setState({
                                appStep: appSteps.submitError
                            })
            );
            return;
        }
        requests.submitFormData(
            this.getFormData(),
            this.state.captchaToken,

            () => {
                this.setState({
                    appStep: appSteps.submitSuccess
                });
            },
            () => {
                this.setState({
                    appStep: appSteps.submitError
                });
            }
        );
    }

    submitDeleteAccountRequest() {
        this.setState({
            appStep: appSteps.loading
        });
        const sessionKeyData = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_AUTH_KEY)).sessionKey;
        const formData = this.getFormData();
        const data = {
            formDataJson: JSON.stringify({
                email: formData.player.email,
                name: formData.player.name,
                type: formData.issueType,
                subType: formData.issueSubType,
                message: formData.message
            }),
            formType: 1,
            formVersion: '1.0.0',
            gameDataJson: null,
            contextDataJson: '',
            source: formData.source,
            soportoFormType: formData.soportoFormType
        };
        requests.submitDeleteAccountRequest(
            data,
            SOPORTO_KING_APP_ID,
            sessionKeyData,
            () => {
                this.setState({
                    appStep: appSteps.submitSuccess
                });
            },
            () => {
                this.setState({
                    appStep: appSteps.submitError
                });
            }
        );
    }

    validateFormData() {
        const formData = this.state.formData;

        this.setState({
            isValidationEnabled: true
        });

        return Object.keys(formData).every((fieldId) => formData[fieldId].isValid);
    }

    fillFormDataFromResponse(response) {
        const { email, fullName } = response.playerData;
        const formData = {};
        if (email) {
            formData.email = { value: email, isValid: true };
        }

        if (fullName) {
            formData.name = { value: fullName, isValid: true };
        }

        if (email || fullName) {
            this.forceFormDataUpdate(formData);
        }
    }

    prepopulateFormData(gameInQuery, platformInQuery, issueTypeInQuery, issueTypeInSubQuery) {
        let formData = {};

        this.prepopulateFormDataWithGameAndPlatform(formData, gameInQuery, platformInQuery);
        this.prepopulateFormDataWithIssueTypeAndIssueSubType(formData, issueTypeInQuery, issueTypeInSubQuery);

        if (Object.keys(formData).length) {
            this.forceFormDataUpdate(formData);
        }
    }

    prepopulateFormDataWithGameAndPlatform(formData, gameInQuery, platformInQuery) {
        if (!gameInQuery) {
            return;
        }

        const selectedGame = selectFieldsConfiguration.games.find((game) => game.id === gameInQuery);

        if (!selectedGame) {
            return;
        }

        formData.game = { value: gameInQuery, isValid: true };

        if (platformInQuery) {
            const gamePlatforms = selectFieldsConfiguration.platforms[gameInQuery];

            if (!gamePlatforms) {
                return;
            }

            const selectedPlatform = gamePlatforms.find((platform) => platform.id === platformInQuery);
            if (selectedPlatform) {
                formData.platform = { value: platformInQuery, isValid: true };
            }
        }
    }

    prepopulateFormDataWithIssueTypeAndIssueSubType(formData, issueTypeInQuery, issueTypeInSubQuery) {
        if (!issueTypeInQuery) {
            return;
        }

        const selectedIssueType = selectFieldsConfiguration.issueTypes.find(
            (issueType) => issueType.id === issueTypeInQuery
        );

        if (!selectedIssueType) {
            return;
        }

        formData.issueType = { value: issueTypeInQuery, isValid: true };

        if (issueTypeInSubQuery) {
            const issueSubTypes = selectFieldsConfiguration.issueSubTypes[issueTypeInQuery];

            if (!issueSubTypes) {
                return;
            }

            const selectedIssueSubType = issueSubTypes.find((issueSubType) => issueSubType.id === issueTypeInSubQuery);
            if (selectedIssueSubType) {
                formData.issueSubType = { value: issueTypeInSubQuery, isValid: true };
            }
        }
    }

    showRtbfForm() {
        this.setState({ appStep: appSteps.rtbf });
    }

    showLogin() {
        this.setState({ appStep: appSteps.login });
        this.clearFormData();
    }

    reload() {
        window.location.reload();
    }

    getViewContent() {
        const getCopy = (key) => {
            return i18n.getCopy(this.state.lang, key);
        };

        switch (this.state.appStep) {
            case appSteps.loading:
                return <LoadingLayer />;
            case appSteps.form:
                return (
                    <Form
                        captchaToken={this.state.captchaToken}
                        formData={this.state.formData}
                        isValidationEnabled={this.state.isValidationEnabled}
                        lang={this.state.lang}
                        onFieldUpdate={this.onFieldUpdate}
                        onFieldHide={this.onFieldHide}
                        onLanguageChange={this.onLanguageChange}
                        onMsgUpdate={this.onMsgFieldUpdate}
                        onSubmitClick={this.onSubmitClick}
                        onSessionKeyUpdate={this.onSessionKeyUpdate}
                        onSourceUpdate={this.onSourceUpdate}
                        onSoportoFormTypeUpdate={this.onSoportoFormTypeUpdate}
                        forceFormDataUpdate={this.forceFormDataUpdate}
                        formType={this.formType}
                    />
                );
            case appSteps.rtbf:
                return (
                    <FormDeleteAccount
                        formData={this.state.formData}
                        isValidationEnabled={this.state.isValidationEnabled}
                        lang={this.state.lang}
                        onFieldUpdate={this.onFieldUpdate}
                        onLanguageChange={this.onLanguageChange}
                        onSubmitClick={this.onDeleteAccountRequest}
                        onSourceUpdate={this.onSourceUpdate}
                        onSoportoFormTypeUpdate={this.onSoportoFormTypeUpdate}
                        forceFormDataUpdate={this.forceFormDataUpdate}
                        formType={this.formType}
                        showLogin={this.showLogin}
                        fillFormDataFromResponse={this.fillFormDataFromResponse}
                    />
                );
            case appSteps.submitError:
                return (
                    <ErrorLayer
                        errorTitle={getCopy('error.title')}
                        errorMsg={getCopy('error.msg.wrong')}
                        tryAgainMsg={getCopy('error.msg.retry')}
                        buttonMsg={getCopy('error.button.retry')}
                        buttonHandler={() =>
                            this.setState({
                                appStep: appSteps.form,
                                isValidationEnabled: false,
                                captchaToken: ''
                            })
                        }
                    />
                );
            case appSteps.submitSuccess:
                return (
                    <SubmitSuccess
                        formTitle={getCopy('form.title')}
                        successTitle={getCopy('success.title')}
                        successMsg={getCopy('success.message')}
                        email={this.state.formData.email.value}
                    />
                );

            case appSteps.login:
                return <Login showRtbfForm={this.showRtbfForm} lang={this.state.lang} />;
            default:
                return <LoadingLayer />;
        }
    }

    render() {
        return (
            <AuthProvider>
                <div className="App">
                    <header className="App-header">
                        <div className="App-header-logo">
                            <img className="App-img" src={logo} alt="King crown logo" />
                        </div>
                        {this.state.appStep === appSteps.rtbf && <Logout lang={this.state.lang} />}
                    </header>
                    {this.getViewContent()}
                    <footer className="App-footer">
                        <LegalInfo
                            lang={this.state.lang}
                            showHelp={[appSteps.rtbf, appSteps.login].includes(this.state.appStep)}
                        />
                    </footer>
                </div>
            </AuthProvider>
        );
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js